import { html, css, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { BaseElement } from '../base/base-element.js';

export type HighlightType = 'orange' | 'purple' | 'blue' | 'none';

@customElement('piano-key')
export class PianoKey extends BaseElement {
    static styles = [
        BaseElement.styles,
        css`
            /* Pulse-glow animation for faded highlights */
            @keyframes pulse-glow {
                0%, 100% { opacity: 1; }
                50% { opacity: 0.4; }
            }

            :host {
                display: block;
                position: relative;
                cursor: pointer;
                transition: background-color 0.1s ease;
            }

            /* === White key === */
            :host(:not([black])) {
                width: 24px;
                height: 120px;
                background: linear-gradient(to bottom, #f8f8f8 0%, #fff 10%, #fff 90%, #e8e8e8 100%);
                border: 1px solid #999;
                border-radius: 0 0 4px 4px;
                margin-right: -1px;
                z-index: 1;
            }

            :host(:not([black]):hover) {
                background: linear-gradient(to bottom, #f0f0f0 0%, #fafafa 10%, #fafafa 90%, #e0e0e0 100%);
            }

            /* === Black key === */
            :host([black]) {
                width: 16px;
                height: 75px;
                background: linear-gradient(to bottom, #333 0%, #1a1a1a 100%);
                border: 1px solid #000;
                border-radius: 0 0 3px 3px;
                margin-left: -8px;
                margin-right: -8px;
                z-index: 2;
            }

            :host([black]:hover) {
                background: linear-gradient(to bottom, #444 0%, #2a2a2a 100%);
            }

            /* === Solid highlights — white keys === */
            :host([highlight="orange"]:not([black])) {
                background: #F39C12;
                border-color: #d68910;
            }

            :host([highlight="purple"]:not([black])) {
                background: #9B59B6;
                border-color: #7d3c98;
            }

            :host([highlight="blue"]:not([black])) {
                background: #3498DB;
                border-color: #2980b9;
            }

            /* === Solid highlights — black keys === */
            :host([highlight="orange"][black]) {
                background: linear-gradient(to bottom, #f5b041 0%, #F39C12 100%);
                border-color: #d68910;
            }

            :host([highlight="purple"][black]) {
                background: linear-gradient(to bottom, #af7ac5 0%, #9B59B6 100%);
                border-color: #7d3c98;
            }

            :host([highlight="blue"][black]) {
                background: linear-gradient(to bottom, #5dade2 0%, #3498DB 100%);
                border-color: #2980b9;
            }

            /* === Faded highlights — white keys === */
            :host([highlight="orange"][faded]:not([black])) {
                background:
                    repeating-linear-gradient(
                        45deg,
                        transparent,
                        transparent 3px,
                        rgba(0, 0, 0, 0.15) 3px,
                        rgba(0, 0, 0, 0.15) 6px
                    ),
                    rgba(243, 156, 18, 1);
                border-color: rgba(214, 137, 16, 0.6);
                animation: pulse-glow 0.4s ease-in-out infinite;
            }

            :host([highlight="purple"][faded]:not([black])) {
                background:
                    repeating-linear-gradient(
                        45deg,
                        transparent,
                        transparent 3px,
                        rgba(0, 0, 0, 0.15) 3px,
                        rgba(0, 0, 0, 0.15) 6px
                    ),
                    rgba(155, 89, 182, 1);
                border-color: rgba(125, 60, 152, 0.6);
                animation: pulse-glow 0.4s ease-in-out infinite;
            }

            /* === Faded highlights — black keys === */
            :host([highlight="orange"][faded][black]) {
                background:
                    repeating-linear-gradient(
                        45deg,
                        transparent,
                        transparent 2px,
                        rgba(0, 0, 0, 0.2) 2px,
                        rgba(0, 0, 0, 0.2) 4px
                    ),
                    linear-gradient(to bottom, rgba(245, 176, 65, 0.5) 0%, rgba(243, 156, 18, 0.5) 100%);
                animation: pulse-glow 0.4s ease-in-out infinite;
            }

            :host([highlight="purple"][faded][black]) {
                background:
                    repeating-linear-gradient(
                        45deg,
                        transparent,
                        transparent 2px,
                        rgba(0, 0, 0, 0.2) 2px,
                        rgba(0, 0, 0, 0.2) 4px
                    ),
                    linear-gradient(to bottom, rgba(175, 122, 197, 0.5) 0%, rgba(155, 89, 182, 0.5) 100%);
                animation: pulse-glow 0.4s ease-in-out infinite;
            }

            /* === Fingering number badge === */
            .fingering-number {
                display: flex;
                position: absolute;
                bottom: 8px;
                left: 50%;
                transform: translateX(-50%);
                width: 18px;
                height: 18px;
                background: rgba(0, 0, 0, 0.7);
                color: #fff;
                border-radius: 50%;
                font-size: 11px;
                font-weight: bold;
                font-family: system-ui, -apple-system, sans-serif;
                align-items: center;
                justify-content: center;
                z-index: 3;
            }

            :host([black]) .fingering-number {
                bottom: 5px;
                width: 14px;
                height: 14px;
                font-size: 9px;
            }

            /* === Responsive: tablet === */
            @media (max-width: 768px) {
                :host(:not([black])) {
                    width: 20px;
                    height: 100px;
                }

                :host([black]) {
                    width: 14px;
                    height: 62px;
                    margin-left: -7px;
                    margin-right: -7px;
                }

                .fingering-number {
                    width: 16px;
                    height: 16px;
                    font-size: 10px;
                    bottom: 6px;
                }

                :host([black]) .fingering-number {
                    width: 12px;
                    height: 12px;
                    font-size: 8px;
                    bottom: 4px;
                }
            }

            /* === Responsive: phone === */
            @media (max-width: 480px) {
                :host(:not([black])) {
                    width: 16px;
                    height: 80px;
                }

                :host([black]) {
                    width: 11px;
                    height: 50px;
                    margin-left: -5.5px;
                    margin-right: -5.5px;
                }

                .fingering-number {
                    width: 14px;
                    height: 14px;
                    font-size: 9px;
                    bottom: 4px;
                }

                :host([black]) .fingering-number {
                    width: 10px;
                    height: 10px;
                    font-size: 7px;
                    bottom: 3px;
                }
            }
        `,
    ];

    @property({ type: Number, reflect: true })
    midi = 0;

    @property({ type: Boolean, reflect: true })
    black = false;

    @property({ type: String, reflect: true })
    highlight: HighlightType = 'none';

    @property({ type: Boolean, reflect: true })
    faded = false;

    @property({ type: String })
    fingering = '';

    render() {
        const showFingering = this.fingering && !this.faded;

        return html`${showFingering
            ? html`<span class="fingering-number">${this.fingering}</span>`
            : nothing}`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'piano-key': PianoKey;
    }
}
